import { z } from 'zod';

import { ZodCurrency } from '@Common/types/Amount';
import { ZodB2bSavingPlanCategory } from '@Common/types/b2b/plans';
import { ZodWithdrawalAllocations } from '@Common/types/b2b/withdraw';
import {
  getB2bWithdrawalBulkPayoutInformationEndpoint,
  postB2bWithdrawalAmountsEndpoint,
  postB2bWithdrawalEligibleAllocationsEndpoint,
  ZodB2bWithdrawalBulkPayoutInformationParams,
  ZodB2bWithdrawalOrdersFlow,
  ZodB2bWithdrawalParams,
  ZodFilters,
  ZodThirdPartyBankAccount,
  zodB2bWithdrawalDestinationAccountWarning,
  zodB2bWithdrawalStatus,
  zodB2bWithdrawalType,
  zodGetB2bWithdrawalsResponse as zodGetB2bWithdrawalsResponseBase,
} from '@Common/types/bo/b2b/withdrawals';
import { BoListQueryBase } from '@Common/types/bo/bo_list_query';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { ZodDate } from '@Common/types/date';
import { ZodB2BEarlyWithdrawalReason } from '@Common/types/funding/withdraw_early_request';
import { ZodMdlrPaymentStatus } from '@Common/types/modulr';
import { MdlrAccountTypeValues } from '@Common/types/qa/modulr';
import { ZodId } from '@Common/types/request';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

const ZodPostBoExternalWithdrawalBulkTemplateBody = z.object({
  instrument_id: ZodId,
});

const ZodPostBoExternalWithdrawalBulkTemplateResponseBody = z.object({
  id: ZodId,
  url: ZodTrimmedString,
  file_name: ZodTrimmedString,
  file_path: ZodTrimmedString,
});

export const postBoExternalWithdrawalBulkTemplateEndpoint = endpoint({
  method: 'POST',
  body: ZodPostBoExternalWithdrawalBulkTemplateBody,
  responseBody: ZodPostBoExternalWithdrawalBulkTemplateResponseBody,
});

export type PostBoExternalWithdrawalBulkTemplateEndpoint = GetEndpointType<
  typeof postBoExternalWithdrawalBulkTemplateEndpoint
>;

const ZodPostBoExternalWithdrawalBulkRow = z.object({
  employee_id: ZodId,
  quantity: z.number().min(0),
});

const ZodPostBoExternalWithdrawalBulkBody = z.object({
  instrument_id: ZodId,
  rows: z.array(ZodPostBoExternalWithdrawalBulkRow).min(1).max(1000),
});

const ZodPostBoExternalWithdrawalBulkResponseBody = z.object({
  id: ZodId,
  created_count: z.number().int(),
});

export const postBoExternalWithdrawalBulkEndpoint = endpoint({
  method: 'POST',
  body: ZodPostBoExternalWithdrawalBulkBody,
  responseBody: ZodPostBoExternalWithdrawalBulkResponseBody,
});

export type PostBoExternalWithdrawalBulkEndpoint = GetEndpointType<
  typeof postBoExternalWithdrawalBulkEndpoint
>;

const ZodPostBoExternalWithdrawalBulkPositionsBody = z.object({
  b2b_company_id: ZodId,
  instrument_id: ZodId,
  employee_ids: z.array(ZodId).min(1).max(100),
});

const ZodPostBoExternalWithdrawalBulkPositionsResponseBody = z.object({
  id: ZodId,
  positions: z.array(
    z.object({
      employee_id: ZodId,
      company_plan_type: ZodTrimmedString.nullable(),
      quantity: z.number(),
      amount: z.number().int(),
      position_date: ZodDate,
      is_deceased: z.boolean(),
    }),
  ),
});

export const postBoExternalWithdrawalBulkPositionsEndpoint = endpoint({
  method: 'POST',
  body: ZodPostBoExternalWithdrawalBulkPositionsBody,
  responseBody: ZodPostBoExternalWithdrawalBulkPositionsResponseBody,
});

export type PostBoExternalWithdrawalBulkPositionsEndpoint = GetEndpointType<
  typeof postBoExternalWithdrawalBulkPositionsEndpoint
>;

export const getBoExternalWithdrawalBulkPayoutInformationEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodB2bWithdrawalBulkPayoutInformationParams,
  responseBody: getB2bWithdrawalBulkPayoutInformationEndpoint.responseBody,
});

export type GetBoExternalWithdrawalBulkPayoutInformationEndpoint = GetEndpointType<
  typeof getBoExternalWithdrawalBulkPayoutInformationEndpoint
>;

const ZodB2bWithdrawalResponseBody = z.object({
  id: ZodId,
  contract_id: ZodId,
  investing_product: ZodB2bSavingPlanCategory,
  b2b_company_plan_ids: z.array(z.number().int()),
  mdlr_beneficiary_id: ZodId.nullable(),
  account_type: z.enum(MdlrAccountTypeValues).nullable(),
  sort_code: ZodTrimmedString.nullable(),
  account_number: ZodTrimmedString.nullable(),
  bank_country: ZodTrimmedString.nullable(),
  status: zodB2bWithdrawalStatus,
  type: zodB2bWithdrawalType,
  orders_flow: ZodB2bWithdrawalOrdersFlow,
  is_planned_withdrawal: z.boolean(),
  gross_amount: z.number().nullable(),
  net_amount: z.number().nullable(),
  tax_amount: z.number().nullable(),
  initial_amount: z.number(),
  early_withdraw_reason: ZodB2BEarlyWithdrawalReason.nullable(),
  early_withdraw_event_date: ZodDate.nullable(),
  currency: ZodCurrency,
  employee_id: ZodId,
  created_at: z.date(),
  updated_at: z.date(),
  paid_at: z.date().nullable(),
  payment_status: ZodMdlrPaymentStatus.nullable(),
  assets_class: z.enum(['ccb', 'mutual-fund']),
  bo_task_id: ZodId,
  destination_account_warning: zodB2bWithdrawalDestinationAccountWarning.nullable(),
  correlation_id: ZodTrimmedString,
});

export const getBoExternalWithdrawalEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodB2bWithdrawalParams,
  responseBody: ZodB2bWithdrawalResponseBody,
});

export type GetBoExternalWithdrawalEndpoint = GetEndpointType<
  typeof getBoExternalWithdrawalEndpoint
>;

export type GetBoExternalWithdrawalResponse =
  GetBoExternalWithdrawalEndpoint['Client']['ResponseBody'];

export const zodGetB2bWithdrawalsResponse = zodGetB2bWithdrawalsResponseBase.element
  .omit({
    user_id: true,
    company_id: true,
    bo_task_id: true,
    task_assignee_group: true,
    task_assignee: true,
    mdlr_beneficiary_id: true,
  })
  .array();
export type GetB2bWithdrawalsResponse = z.TypeOf<typeof zodGetB2bWithdrawalsResponse>;

export const getB2bWithdrawalsSortColumns: (keyof GetB2bWithdrawalsResponse[number])[] = [
  'id',
  'contract_id',
  'account_type',
  'sort_code',
  'account_number',
  'bank_country',
  'status',
  'orders_flow',
  'gross_amount',
  'net_amount',
  'tax_amount',
  'initial_amount',
  'currency',
  'created_at',
  'updated_at',
  'correlation_id',
];

const ZodBoExternalFilters = ZodFilters.omit({
  user_id: true,
  company_id: true,
  mdlr_beneficiary_id: true,
  bo_task_id: true,
  task_assignee: true,
});

export const ZodGetB2bWithdrawalsQuery = buildZodBoListQueryParsed(
  ZodBoExternalFilters,
  getB2bWithdrawalsSortColumns,
  { range: [0, 10000] },
);

export type GetB2bWithdrawalsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodBoExternalFilters>,
  (typeof getB2bWithdrawalsSortColumns)[number]
>;

export const postBoExternalWithdrawalCancelEndpoint = endpoint({
  method: 'POST',
  body: z.object({ id: ZodId }),
  responseBody: z.object({ id: ZodId }),
});
export type PostBoExternalWithdrawalCancelEndpoint = GetEndpointType<
  typeof postBoExternalWithdrawalCancelEndpoint
>;

export const getB2bWithdrawalsExternalEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetB2bWithdrawalsQuery,
  responseBody: zodGetB2bWithdrawalsResponse,
});
export type GetB2bWithdrawalsExternalEndpoint = GetEndpointType<
  typeof getB2bWithdrawalsExternalEndpoint
>;

const ZodBoExternalWithdrawalDocumentBase = z.object({
  type: z.enum(['early_withdrawal_request', 'proof_of_identity']),
  name: ZodTrimmedString,
  date_of_expiry: ZodDate.optional(),
});

const ZodBoExternalWithdrawalDocument = z.union([
  z.object({ ...ZodBoExternalWithdrawalDocumentBase.shape, file: ZodTrimmedString }),
  z.object({ ...ZodBoExternalWithdrawalDocumentBase.shape, presigned_path: ZodTrimmedString }),
]);

const ZodPostBoExternalWithdrawalRequestBody = z
  .object({
    employee_id: ZodId,
    beneficiary_id: ZodId.optional(),
    third_party_bank_account: ZodThirdPartyBankAccount.optional(),
    early_event_date: ZodDate.nullable(),
    early_reason: ZodB2BEarlyWithdrawalReason.nullable(),
    allocations: ZodWithdrawalAllocations,
    documents: z.array(ZodBoExternalWithdrawalDocument),
  })
  .refine(
    (data) => {
      const hasBeneficiary = data.beneficiary_id !== undefined;
      const hasThirdParty = data.third_party_bank_account !== undefined;
      return hasBeneficiary !== hasThirdParty;
    },
    { message: 'Either beneficiary_id or third_party_bank_account must be provided' },
  )
  .refine((data) => data.early_event_date === null || data.early_reason !== null, {
    message: 'early_reason is required for early withdrawals',
    path: ['early_reason'],
  });

export const postBoExternalWithdrawalRequestEndpoint = endpoint({
  method: 'POST',
  body: ZodPostBoExternalWithdrawalRequestBody,
  responseBody: z.object({ id: ZodId }),
});
export type PostBoExternalWithdrawalRequestEndpoint = GetEndpointType<
  typeof postBoExternalWithdrawalRequestEndpoint
>;

const ZodPostBoExternalWithdrawalAmountsBody = z.object({
  employee_id: ZodId,
  early_event_date: ZodDate.nullable().optional(),
  early_reason: ZodB2BEarlyWithdrawalReason.nullable().optional(),
  allocations: ZodWithdrawalAllocations,
});

export const postBoExternalWithdrawalAmountsEndpoint = endpoint({
  method: 'POST',
  body: ZodPostBoExternalWithdrawalAmountsBody,
  responseBody: postB2bWithdrawalAmountsEndpoint.responseBody,
});
export type PostBoExternalWithdrawalAmountsEndpoint = GetEndpointType<
  typeof postBoExternalWithdrawalAmountsEndpoint
>;

const ZodPostBoExternalWithdrawalEligibleAllocationsBody = z.object({
  employee_id: ZodId,
  products: z
    .array(ZodB2bSavingPlanCategory)
    .min(1)
    .refine(
      (products) => {
        const uniqueProducts = new Set(products);
        return products.length === uniqueProducts.size;
      },
      { message: 'Products must be unique', path: ['products'] },
    ),
  early_event_date: ZodDate,
  early_reason: ZodB2BEarlyWithdrawalReason,
});

export const postBoExternalWithdrawalEligibleAllocationsEndpoint = endpoint({
  method: 'POST',
  body: ZodPostBoExternalWithdrawalEligibleAllocationsBody,
  responseBody: postB2bWithdrawalEligibleAllocationsEndpoint.responseBody,
});
export type PostBoExternalWithdrawalEligibleAllocationsEndpoint = GetEndpointType<
  typeof postBoExternalWithdrawalEligibleAllocationsEndpoint
>;
